import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_URL } from "../config";
import { useI18n } from "../i18n/I18nProvider";

const PAGE_ORDER = ["home", "contact", "blog", "about", "portfolio"];

const PAGE_LABEL_KEYS = {
  home: "nav.home",
  contact: "nav.contact",
  blog: "nav.blog",
  about: "nav.about",
  portfolio: "nav.portfolio",
};

const View = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();

  const rows = useMemo(() => {
    return PAGE_ORDER.map((page) => ({
      page,
      label: t(PAGE_LABEL_KEYS[page] || page),
      views: stats[page]?.views ?? 0,
      ips: stats[page]?.ips ?? [],
    }));
  }, [stats, t]);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setLoading(true);
      const results = {};
      for (const page of PAGE_ORDER) {
        try {
          const [resViews, resIps] = await Promise.all([
            axios.get(`${API_URL}/api/view/${page}`),
            axios.get(`${API_URL}/api/view/ips/${page}`),
          ]);
          results[page] = {
            views: resViews.data.views || 0,
            ips: resIps.data.ips || [],
          };
        } catch (err) {
          console.error(err);
          results[page] = { views: 0, ips: [] };
        }
      }
      if (!cancelled) {
        setStats(results);
        setLoading(false);
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="views-stats-page">
      <section className="views-stats-page__section layout_padding">
        <div className="container">
          <div className="views-stats-page__head">
            <h1 className="views-stats-page__title">{t("viewStats.heading")}</h1>
          </div>

          <div className="views-stats-page__card">
            {loading ? (
              <p className="views-stats-page__loading" role="status">
                {t("viewStats.loading")}
              </p>
            ) : (
              <div className="views-stats-page__table-scroll">
                <table className="views-stats-table">
                  <thead>
                    <tr>
                      <th scope="col">{t("viewStats.colPage")}</th>
                      <th scope="col" className="views-stats-table__num">
                        {t("viewStats.colViews")}
                      </th>
                      <th scope="col">{t("viewStats.colIps")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ page, label, views, ips }) => (
                      <tr key={page}>
                        <td>
                          <span className="views-stats-table__page">{label}</span>
                          <span className="views-stats-table__slug">{page}</span>
                        </td>
                        <td className="views-stats-table__num">
                          <span className="views-stats-table__badge">{views}</span>
                        </td>
                        <td className="views-stats-table__ips">
                          {ips.length === 0 ? (
                            <span className="views-stats-table__empty">
                              {t("viewStats.noIps")}
                            </span>
                          ) : (
                            <ul className="views-stats-table__ip-list">
                              {ips.map((ip, index) => (
                                <li key={`${page}-${ip}-${index}`}>
                                  <code>{ip}</code>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default View;
